









































import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { Validate } from 'vuelidate-property-decorators'
import { required, minValue, maxValue } from 'vuelidate/lib/validators'
import { emitEvent, unregisterEvent, registerEvent, AxEventType } from '@aspenware-library/event-hub'
import { Getter } from 'vuex-class'

@Component({
  name: 'MonerisPaymentInfo',
})
export default class MonerisPaymentInfo extends Vue {
  @Getter('global/labels') labels

  @Prop({ default: 'Credit Card Number' }) cardNumberLabel: string
  @Prop({ default: 'Exp. Date' }) expLabel: string
  @Prop({ default: 'CVD' }) cvdLabel: string
  @Prop({ default: 'Card number is invalid' }) cardNumberInvalidTitle: string
  @Prop({ default: 'Must be numeric and the correct number of digits' }) cardNumberInvalidMessage: string
  @Prop({ default: 'Expiration date is invalid' }) expInvalidTitle: string
  @Prop({ default: 'Must be in the format MMYY and be current month or in the future' }) expInvalidMessage: string
  @Prop({ default: 'CVD number is invalid' }) cvdInvalidTitle: string
  @Prop({ default: 'Must be 3-4 digits' }) cvdInvalidMessage: string
  @Prop({ default: 'Payment provider' }) tokenInvalidTitle: string
  @Prop({ default: 'There was a problem validating your card info' }) tokenInvalidMessage: string
  @Prop({ default: '' }) profileId: string
  @Prop({ default: 'helvetica, arial, sans-serif' }) fontFamily: string
  @Prop({ default: true }) requiresBillingAddress: boolean
  @Prop({ default: false }) sandboxMode: boolean
  @Prop({ default: false }) testFailedTokenResponse: boolean

  private showCover: boolean = false
  private sandboxMonerisUrl: string = 'https://esqa.moneris.com/HPPtoken/index.php'
  private productionMonerisUrl: string = 'https://www3.moneris.com/HPPtoken/index.php'
  private responseMessage: string = ''
  private responseCode: string[] = []
  private responseSource: string = ''
  private responseKey: string = ''
  private responseBin: string = ''
  private errorCodes: any = {
    '001': 'Successful creation of temporary token',
    '940': 'Invalid profile id (on tokenization request)',
    '941': 'Error generating token',
    '942': 'Invalid Profile ID, or source URL ',
    '943': 'Card data is invalid (not numeric, fails mod10, we will remove spaces)',
    '944': 'Invalid expiration date (mmyy, must be current month or in the future)',
    '945': 'Invalid CVD data (not 3-4 digits)',
  }
  private labelTypes: any = {
    standard: 1,
    placeholder: 2,
  }

  private paymentInfoUpdated() {
    emitEvent('ASPENWARE_PAYMENT_INFO_UPDATED', {
      Nonce: this.responseKey,
      CardNumber: this.responseBin,
      PaymentType: 'Payments.Moneris',
      CardHolderName: ``,
      CreditCard: this.responseKey,
      ExpirationDate: ``,
      CardType: this.cardType,
    })
  }

  private tokenUpdated() {
    // TODO: Fix this tech debt by typing $v
    // @ts-expect-error $v is not defined
    if (this.$v.$invalid) {
      emitEvent('ASPENWARE_PAYMENT_TOKEN_RECEIVED', {
        token: null,
        source: 'MONERIS_CC',
        error: 'There was a problem validating your credit card',
      })
    } else {
      this.$emit('monerisResponseReceived', {
        responseMessage: this.responseMessage,
        responseCode: this.responseCode,
        responseKey: this.responseKey,
        responseBin: this.responseBin,
        cardType: this.cardType,
      })

      emitEvent('ASPENWARE_PAYMENT_TOKEN_RECEIVED', {
        token: this.responseKey,
        source: 'MONERIS_CC',
        error: null,
      })
    }
  }

  private notifyListenersMonerisEnabled() {
    window.dispatchEvent(
      new CustomEvent('payment:payment-configuration:updated', {
        detail: {
          requiresBillingAddress: this.requiresBillingAddress,
        },
      })
    )
  }

  get frameSource(): string {
    return `${this.monerisUrl}?id=${this.profileId}&css_body=padding:2px;&display_labels=${this.labelType}&css_input_label=${this.labelStyle}&pmmsg=true&pan_label=${this.cardNumberLabel}&css_textbox=${this.inputStyle}&css_textbox_pan=${this.creditCardNumberStyle}&enable_exp=1&exp_label=${this.expLabel}&css_textbox_exp=${this.expirationStyle}&css_label_exp=${this.expirationLabelStyle}&enable_cvd=1&css_textbox_cvd=${this.cvdStyle}&css_label_cvd=${this.cvdLabelStyle}&cvd_label=${this.cvdLabel}`
  }

  get labelStyle(): string {
    const style = {
      'margin-bottom': '10px',
      'display': 'block',
      'font-size': '0.875em',
      'font-weight': 'bold',
      'text-transform': 'uppercase',
      'font-family': `${this.fontFamily}`,
      'color': 'rgba(0,0,0,0.5)',
      'letter-spacing': '0.25px',
    }

    return this.getStyleString(style)
  }

  get labelType(): number {
    return this.labelTypes.standard
  }

  get inputStyle(): string {
    const style = {
      'font-size': '1rem',
      'font-family': `Monospace, ${this.fontFamily}`,
      'border-width': '1px',
      'border-style': 'solid',
      'padding': '0 10px',
      'margin-bottom': '0.75rem',
      'height': '52px',
      'line-height': '52px',
      'box-shadow':
        '0 2px 2px 0 rgba(0, 0, 0, 0.04), 0 1px 5px 0 rgba(0, 0, 0, 0.04), 0 3px 1px -2px rgba(0, 0, 0, 0.1)',
    }

    return this.getStyleString(style)
  }

  get creditCardNumberStyle(): string {
    const style = {
      'width': '100%',
      'margin-bottom': '36px',
      'padding-left': '50px',
      'font-family': 'monospace, helvetica, arial, sans-serif',
      'letter-spacing': '1px',
      'background':
        'transparent url(https://aspenware.com/wp-content/uploads/2021/07/i-card-front-grey.svg) no-repeat 10px',
      'background-size': 'auto 40%',
    }

    return this.getStyleString(style)
  }

  get expirationStyle(): string {
    const widthStyle = `width:calc(50% - 16px);`
    const displayStyle = `display:inline-block;`
    const marginStyle = `margin-right:30px;`

    return `${widthStyle}${displayStyle}${marginStyle}`
  }

  get expirationLabelStyle(): string {
    const style = {
      position: 'absolute',
      top: '92px',
    }

    return this.getStyleString(style)
  }

  get cvdStyle(): string {
    const widthStyle = `width:calc(50% - 16px);`
    const displayStyle = `display:inline-block;`

    return `${widthStyle}${displayStyle}`
  }

  get cvdLabelStyle(): string {
    const style = {
      'position': 'absolute',
      'top': '92px',
      'left': 'calc(50% - 16px)',
      'margin-left': '30px',
    }

    return this.getStyleString(style)
  }

  get monerisUrl(): string {
    return this.sandboxMode ? this.sandboxMonerisUrl : this.productionMonerisUrl
  }

  get cardType(): string {
    const binNumber = this.responseBin

    if (binNumber) {
      // visa
      let re = new RegExp('^4')
      if (binNumber.match(re) != null) {
        return 'Visa'
      }

      // Mastercard
      // Updated for Mastercard 2017 BINs expansion
      const paddedBin = binNumber + '0000000000'
      if (
        /^(5[1-5][0-9]{14}|2(22[1-9][0-9]{12}|2[3-9][0-9]{13}|[3-6][0-9]{14}|7[0-1][0-9]{13}|720[0-9]{12}))$/.test(
          paddedBin
        )
      ) {
        return 'Mastercard'
      }

      // AMEX
      re = new RegExp('^3[47]')
      if (binNumber.match(re) != null) {
        return 'AMEX'
      }

      // Discover
      re = new RegExp('^(6011|622(12[6-9]|1[3-9][0-9]|[2-8][0-9]{2}|9[0-1][0-9]|92[0-5]|64[4-9])|65)')
      if (binNumber.match(re) != null) {
        return 'Discover'
      }

      // Diners
      re = new RegExp('^36')
      if (binNumber.match(re) != null) {
        return 'Diners'
      }

      // Diners - Carte Blanche
      re = new RegExp('^30[0-5]')
      if (binNumber.match(re) != null) {
        return 'Diners - Carte Blanche'
      }

      // JCB
      re = new RegExp('^35(2[89]|[3-8][0-9])')
      if (binNumber.match(re) != null) {
        return 'JCB'
      }

      // Visa Electron
      re = new RegExp('^(4026|417500|4508|4844|491(3|7))')
      if (binNumber.match(re) != null) {
        return 'Visa Electron'
      }
    }

    return ''
  }

  @Watch('frameSource')
  onFrameSourceChanged() {
    this.resetMoneris()
  }

  private getStyleString(styles): string {
    let styleString = ''
    for (const key in styles) {
      if (styles.hasOwnProperty(key)) {
        styleString += `${key}: ${styles[key]};`
      }
    }
    return styleString
  }

  @Validate({
    tokenValid: (monerisInfo) => monerisInfo.tokenValid,
    cardNumberValid: (monerisInfo) => monerisInfo.cardNumberValid,
    expDateValid: (monerisInfo) => monerisInfo.expDateValid,
    cvdValid: (monerisInfo) => monerisInfo.cvdValid,
  })
  private monerisValidation: any = {
    tokenValid: true,
    cardNumberValid: true,
    expDateValid: true,
    cvdValid: true,
  }

  private resetMoneris() {
    // Reset validation
    const keys = Object.keys(this.monerisValidation)
    keys.forEach((key, index) => {
      this.monerisValidation[key] = true
    })

    // Reset responses
    this.responseMessage = ''
    this.responseCode = []
    this.responseKey = ''
    this.responseBin = ''

    this.$emit('monerisResponseReceived', {
      responseMessage: this.responseMessage,
      responseCode: this.responseCode,
      responseKey: this.responseKey,
      responseBin: this.responseBin,
      cardType: this.cardType,
    })
  }

  private doMonerisSubmit() {
    // Use this typeguard and function to ensure that the HTMLElement is of type HTMLIFrameElement
    const isIFrame = (input: HTMLElement | null): input is HTMLIFrameElement =>
      input !== null && input.tagName === 'IFRAME'

    const monFrameRef = document.getElementById('monerisFrame')
    if (isIFrame(monFrameRef) && monFrameRef.contentWindow) {
      const frameWindow = monFrameRef.contentWindow
      frameWindow.postMessage('tokenize', this.monerisUrl)
    }
    // change link according to table above
    return false
  }

  private respMsg(e) {
    // Reset validation before attempting to tokenize with Moneris
    this.responseSource = e.origin
    // Other window events might trigger this function. Ensure the message is coming from the
    // Moneris iFrame window
    if (this.responseSource.includes('moneris.com')) {
      this.resetMoneris()
      const response = JSON.parse(e.data)
      this.responseMessage = response.errorMessage
      this.responseCode = response.responseCode
      this.responseKey = response.dataKey
      this.responseBin = response.bin

      if (this.testFailedTokenResponse) {
        this.responseCode.push('941')
        this.responseCode = this.responseCode.filter((c) => c !== '001')
        this.responseKey = ''
        this.responseBin = ''
      }

      if (this.responseCode && this.responseCode[0] === '001') {
        this.responseMessage = this.errorCodes['001']
      }
      if (this.responseCode.includes('941')) {
        this.monerisValidation.tokenValid = false
      }
      if (this.responseCode.includes('943')) {
        this.monerisValidation.cardNumberValid = false
      }
      if (this.responseCode.includes('944')) {
        this.monerisValidation.expDateValid = false
      }
      if (this.responseCode.includes('945')) {
        this.monerisValidation.cvdValid = false
      }
      // TODO: Fix this tech debt by typing $v
      // @ts-expect-error $v is not defined
      this.$v.$touch()

      this.paymentInfoUpdated()
      this.tokenUpdated()
    }
  }

  private resetPayment() {
    emitEvent('ASPENWARE_PAYMENT_VALIDATION_CHANGED', true)
    emitEvent('ASPENWARE_PAYMENT_INFO_UPDATED', {
      Nonce: null,
      CardNumber: null,
      PaymentType: 'Payments.Moneris',
      CardHolderName: null,
      CreditCard: null,
      ExpirationDate: null,
      CardType: null,
      ZipPostalCode: null,
    })
  }

  private setupMoneris() {
    emitEvent('ASPENWARE_PAYMENT_REQUIRED_TOKEN', 'MONERIS_CC')
  }

  created() {
    registerEvent<void>('ASPENWARE_PAYMENT_REQUEST_TOKEN', this.doMonerisSubmit)
  }

  destroyed() {
    unregisterEvent<void>('ASPENWARE_PAYMENT_REQUEST_TOKEN', this.doMonerisSubmit)
    unregisterEvent<void>('ASPENWARE_BALANCE_ZERO_NO_CC_NEEDED')
  }

  mounted() {
    window.addEventListener('message', this.respMsg, false)
    registerEvent('ASPENWARE_BALANCE_ZERO_NO_CC_NEEDED', this.onAWBalZeroNoCCNeeded)
  }

  onAWBalZeroNoCCNeeded(event) {
    this.showCover = event.showCover
  }

  activated() {
    this.resetPayment()
    this.setupMoneris()
    this.notifyListenersMonerisEnabled()
  }
}
